/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { Button, Card } from "@jhool-io/fe-components";
import { useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";

import { useGetClaim } from "../../hooks/claims.queries";
import { useUpdateClaimContent } from "../../hooks/claims.mutations";
import {
    IClaimDetailsContent,
    IClaimInsuranceType,
} from "../../types/claims.types";
import PersonalInfoForm from "./PersonalInfoForm/PersonalInfoForm";

import ServicesInfoForm from "./ServicesInfoForm/ServicesInfoForm";
import useToast from "../../../../hooks/useToast";
import ListState from "../../../../components/ListState/ListState";
import { formatDate } from "../../../../utils/helpers";
import Loader from "../../../../shared-ui/Loader/Loader";
import DiagnosisInfoForm from "./DiagnosisInfoForm/DiagnosisInfoForm";
import { formatClaimServiceDate } from "../../helpers/claims.helper";

const schema = yup.object().shape({
    // personal info
    insuranceType: yup.string(),
    subscriberId: yup.string(),
    subscriberName: yup.string(),
    subscriberAddress: yup.string(),
    subscriberCity: yup.string(),
    subscriberState: yup.string(),
    subscriberPhone: yup.string(),
    subscriberZip: yup.string(),
    subscriberPolicyGroup: yup.string(),
    insuranceGroupName: yup.string(),
    subscriberBirthDate: yup.string(),
    subscriberSex: yup.string(),
    patientName: yup.string(),
    patientAddress: yup.string(),
    patientCity: yup.string(),
    patientState: yup.string(),
    patientPhone: yup.string(),
    patientZip: yup.string(),
    patientBirthDate: yup.string(),
    patientSex: yup.string(),
    patientRelationshipToSubscriber: yup.string(),
    isConditionRelatedToEmployment: yup.string(),
    isConditionAutoAccidentRelated: yup.string(),
    autoAccidentState: yup.string(),
    isConditionOtherAccidentRelated: yup.string(),
    otherInsurancePlanProgram: yup.string(),
    otherSubscriberName: yup.string(),
    otherSubscriberId: yup.string(),
    otherInsurancePlanName: yup.string(),
    patientSignature: yup.string(),
    patientSignatureDate: yup.string(),
    subscriberSignature: yup.string(),

    // diagnosis info
    outsideLabCharge: yup.string(),
    diagnosisCode1: yup.string(),
    diagnosisCode2: yup.string(),
    diagnosisCode3: yup.string(),
    diagnosisCode4: yup.string(),
    diagnosisCode5: yup.string(),
    diagnosisCode6: yup.string(),
    diagnosisCode7: yup.string(),
    diagnosisCode8: yup.string(),
    diagnosisCode9: yup.string(),
    diagnosisCode10: yup.string(),
    diagnosisCode11: yup.string(),
    diagnosisCode12: yup.string(),

    // services info
    federalTaxIdType: yup.string(),
    totalCharge: yup.number(),
    amountPaid: yup.number(),
    federalTaxId: yup.string(),
    claimReferenceOrPatientAcctNo: yup.string(),
    acceptAssignment: yup.string(),
    renderingProviderFirstName: yup.string(),
    renderingProviderSignatureDate: yup.string(),
    billingProviderNPI: yup.string(),
    billingProviderName: yup.string(),
    billingProviderAddress1: yup.string(),
    billingProviderCity: yup.string(),
    billingProviderState: yup.string(),
    billingProviderPhone: yup.string(),
    billingProviderZipCode: yup.string(),
});

interface EditClaimsProps {
    claimToEditId: string;
}

export default function EditClaims({ claimToEditId }: EditClaimsProps) {
    const [searchParams, setSearchParams] = useSearchParams();

    const params = useParams();
    const { noteId } = params;

    const updateClaimContent = useUpdateClaimContent();

    const { data, isLoading, error, isSuccess } = useGetClaim(
        { claim_id: claimToEditId },
        Boolean(claimToEditId)
    );

    const { toast } = useToast();

    const claimContent = data?.data?.[0]?.claim_content;

    const queryClient = useQueryClient();
    const { register, setValue, formState, watch, control, handleSubmit } =
        useForm<IClaimDetailsContent>({
            resolver: yupResolver(schema),
            mode: "onChange",
            defaultValues: {
                insuranceType:
                    (claimContent?.insuranceType as IClaimInsuranceType) ??
                    undefined,
                subscriberId: claimContent?.subscriberId ?? "",
                subscriberFirstName: claimContent?.subscriberFirstName ?? "",
                subscriberMiddleName: claimContent?.subscriberMiddleName ?? "",
                subscriberLastName: claimContent?.subscriberLastName ?? "",
                subscriberAddress: claimContent?.subscriberAddress ?? "",
                subscriberCity: claimContent?.subscriberCity ?? "",
                subscriberState: claimContent?.subscriberState ?? "",
                subscriberPhone: claimContent?.subscriberPhone ?? "",
                patientFirstName: claimContent?.patientFirstName ?? "",
                patientMiddleName: claimContent?.patientMiddleName ?? "",
                patientLastName: claimContent?.patientLastName ?? "",
                subscriberZip: claimContent?.subscriberZip ?? "",
                subscriberPolicyGroup:
                    claimContent?.subscriberPolicyGroup ?? "",

                insuranceGroupName: claimContent?.insuranceGroupName ?? "",
                subscriberSex: claimContent?.subscriberSex ?? "",
                subscriberBirthdate: claimContent?.subscriberBirthdate
                    ? formatDate(
                          claimContent.subscriberBirthdate,
                          true,
                          "MM/dd/yyyy"
                      )
                    : undefined,
                patientAddress: claimContent?.patientAddress ?? "",
                patientCity: claimContent?.patientCity ?? "",
                patientState: claimContent?.patientState ?? "",
                patientPhone: claimContent?.patientPhone ?? "",
                patientZip: claimContent?.patientZip ?? "",
                patientSex: claimContent?.patientSex ?? "",
                patientBirthdate: claimContent?.patientBirthdate
                    ? formatDate(
                          claimContent.patientBirthdate,
                          true,
                          "MM/dd/yyyy"
                      )
                    : undefined,
                patientRelationshipToSubscriber:
                    claimContent?.patientRelationshipToSubscriber ?? undefined,
                isConditionRelatedToEmployment:
                    claimContent?.isConditionRelatedToEmployment || "N",
                isConditionAutoAccidentRelated:
                    claimContent?.isConditionAutoAccidentRelated || "N",
                autoAccidentState: claimContent?.autoAccidentState ?? undefined,
                isConditionOtherAccidentRelated:
                    claimContent?.isConditionOtherAccidentRelated || "N",
                otherInsurancePlanProgram:
                    claimContent?.otherInsurancePlanProgram ?? "",
                otherSubscriberId: claimContent?.otherSubscriberId ?? "",
                otherSubscriberFirstName: `${
                    claimContent?.otherSubscriberFirstName ?? ""
                } ${claimContent?.otherSubscriberMiddleName ?? ""} ${
                    claimContent?.otherSubscriberLastName
                }`,
                otherClaimId: claimContent?.otherClaimId ?? "",
                patientSignature: claimContent?.patientSignature ?? "",
                patientSignatureDate:
                    claimContent?.patientSignatureDate ?? undefined,
                subscriberSignature: claimContent?.subscriberSignature ?? "",
                totalCharge: claimContent?.totalCharge ?? undefined,
                amountPaid: claimContent?.amountPaid ?? undefined,
                federalTaxIdType: claimContent?.federalTaxIdType ?? "",
                federalTaxId: claimContent?.federalTaxId ?? "",
                claimReferenceOrPatientAcctNo:
                    claimContent?.claimReferenceOrPatientAcctNo ?? "",
                acceptAssignment: claimContent?.acceptAssignment ?? "",
                renderingProviderSignatureDate:
                    claimContent?.renderingProviderSignatureDate
                        ? formatDate(
                              claimContent.renderingProviderSignatureDate,
                              true,
                              "MM/dd/yyyy"
                          )
                        : undefined,
                facilityName: claimContent?.facilityName ?? "",
                facilityAddress1: claimContent?.facilityAddress1 ?? "",
                facilityCity: claimContent?.facilityCity ?? "",
                facilityState: claimContent?.facilityState ?? "",
                facilityZip: claimContent?.facilityZip ?? "",
                facilityNPI: claimContent?.facilityNPI ?? "",
                billingProviderName: claimContent?.billingProviderName ?? "",
                billingProviderAddress1:
                    claimContent?.billingProviderAddress1 ?? "",
                billingProviderCity: claimContent?.billingProviderCity ?? "",
                billingProviderState: claimContent?.billingProviderState ?? "",
                billingProviderPhone: claimContent?.billingProviderPhone ?? "",
                billingProviderNPI: claimContent?.billingProviderNPI ?? "",
                billingProviderZipCode:
                    claimContent?.billingProviderZipCode ?? "",
                serviceDetails: claimContent?.serviceDetails ?? undefined,
                outsideLabCharge: claimContent?.outsideLabCharge ?? "",
                referringProviderFirstName:
                    claimContent?.referringProviderFirstName ?? "",
                referringProviderLastName:
                    claimContent?.referringProviderLastName ?? "",
                referringProviderMiddleName:
                    claimContent?.referringProviderMiddleName ?? "",
                referringProviderNumQual:
                    claimContent?.referringProviderNumQual,
                referringProviderNPI: claimContent?.referringProviderNPI ?? "",
                additionalClaimInfo: claimContent?.additionalClaimInfo ?? "",
                resubmissionCode: claimContent?.resubmissionCode ?? "",
                originalClaimReferenceNumber:
                    claimContent?.originalClaimReferenceNumber ?? "",
                diagnosisCode1: claimContent?.diagnosisCode1 ?? "",
                diagnosisCode2: claimContent?.diagnosisCode2 ?? "",
                diagnosisCode3: claimContent?.diagnosisCode3 ?? "",
                diagnosisCode4: claimContent?.diagnosisCode4 ?? "",
                diagnosisCode5: claimContent?.diagnosisCode5 ?? "",
            },
        });

    const onSubmit = (payload: IClaimDetailsContent) => {
        const updateClaimContentPayload = {
            claim_id: claimToEditId,
            claim_content_to_update: {
                ...payload,
                referringProviderFirstName: undefined,
                referringProviderLastName: undefined,
                patientSignatureDate: payload.patientSignatureDate
                    ? formatDate(
                          payload.patientSignatureDate,
                          true,
                          "MM/dd/yyyy"
                      )
                    : "",
                serviceDetails: payload.serviceDetails.map((detail) => ({
                    ...detail,
                    fromDate: claimContent?.serviceDetails[0]?.fromDate
                        ? formatClaimServiceDate(
                              claimContent.serviceDetails[0].fromDate
                          )
                        : "",
                    toDate: claimContent?.serviceDetails[0]?.toDate
                        ? formatClaimServiceDate(
                              claimContent.serviceDetails[0].toDate
                          )
                        : "",
                    modifiers: detail.modifiers
                        ? detail.modifiers.map((mod) => mod || "") // Ensure no null values
                        : [],
                })),
            },
        };

        updateClaimContent.mutate(updateClaimContentPayload, {
            onSuccess: (res) => {
                queryClient.invalidateQueries([
                    "get-claim",
                    { claim_id: claimToEditId },
                ]);

                queryClient.invalidateQueries([
                    "get-claim",
                    { note_id: noteId },
                ]);

                toast({
                    mode: "success",
                    message:
                        res.message || "Claim content updated successfully",
                });

                searchParams.set("claims_view", "view");
                setSearchParams(searchParams);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.message ||
                        "Could not update claim content at this time. Please try again.",
                });
            },
        });
    };

    const onCancel = () => {
        searchParams.set("claims_view", "view");
        searchParams.delete("claim_id");
        setSearchParams(searchParams);
    };

    return (
        <>
            {isLoading && <Loader />}

            {error && error?.response?.status !== 404 && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot edit claim at this time. Please try again later`
                    }
                />
            )}

            {isSuccess && data?.data ? (
                <div className="mt-[--spacing-header]">
                    <Card>
                        <form
                            className="rounded-md"
                            id="update-claim-content"
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <Card classNames="[border:none] ![border-bottom:1px_solid_var(--color-stroke)] rounded-none pb-[--spacing-items] flex gap-4 md:gap-0 flex-col md:flex-row justify-between md:items-center sticky top-0 z-[4]">
                                <h3 className="text-base font-bold">
                                    Edit Claims Info
                                </h3>

                                <div className="flex items-center gap-[--spacing-items]">
                                    <Button
                                        variant="secondary"
                                        onClick={onCancel}
                                        disabled={updateClaimContent.isLoading}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={updateClaimContent.isLoading}
                                    >
                                        Save changes
                                    </Button>
                                </div>
                            </Card>

                            <PersonalInfoForm
                                claimDetails={data.data[0]}
                                register={register}
                                formState={formState}
                                watch={watch}
                                control={control}
                            />

                            <DiagnosisInfoForm
                                claimDetails={data.data[0]}
                                register={register}
                                formState={formState}
                                setValue={setValue}
                                watch={watch}
                                control={control}
                            />

                            <ServicesInfoForm
                                claimDetails={data.data[0]}
                                register={register}
                                formState={formState}
                                control={control}
                                watch={watch}
                            />
                        </form>
                    </Card>
                </div>
            ) : null}
        </>
    );
}
